// Referral code capture & retrieval (cookie + localStorage backup).
// MVP: stores only the affiliate code. No PII. SameSite=Lax, 90 days.

const COOKIE_NAME = 'lr_ref';
const STORAGE_KEY = 'lr_ref';
const MAX_AGE_DAYS = 90;
// Only ASCII letters, digits, dash, underscore. Length 2-32. Anything else is rejected.
const VALID_CODE = /^[A-Za-z0-9_-]{2,32}$/;

function setCookie(value: string) {
  if (typeof document === 'undefined') return;
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function clearCookie() {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/** Capture ?ref=CODE from current URL into cookie + localStorage. Safe to call repeatedly. */
export function captureRefFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ref');
    if (!raw) return;
    if (!VALID_CODE.test(raw)) return;
    setCookie(raw);
    try { localStorage.setItem(STORAGE_KEY, raw); } catch { /* ignore */ }
    recordClickOnce(raw);
  } catch {
    // never throw from referral capture
  }
}

/**
 * Log one referral-link click per code per browser session (counts only, no PII).
 * Fire-and-forget: never blocks, never throws.
 */
function recordClickOnce(code: string): void {
  const key = `lr_ref_click_${code.toUpperCase()}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  import('@/integrations/supabase/client')
    .then(({ supabase }) => supabase.rpc('record_affiliate_click', { p_code: code }))
    .catch(() => { /* ignore */ });
}


/** Retrieve the stored ref code, if any. Cookie wins, localStorage is fallback. */
export function getStoredRef(): string | null {
  const fromCookie = readCookie();
  if (fromCookie && VALID_CODE.test(fromCookie)) return fromCookie;
  try {
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage && VALID_CODE.test(fromStorage)) return fromStorage;
  } catch { /* ignore */ }
  return null;
}

/** Clear stored ref after attribution (or on rollback). */
export function clearStoredRef(): void {
  clearCookie();
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
